// Angular
import { Injectable } from '@angular/core';
// RxJS
import { of, forkJoin } from 'rxjs';
import { mergeMap, map, withLatestFrom, filter, catchError } from 'rxjs/operators';
// NGRX
import { Effect, Actions, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
// Services
import { AgreementService } from '../_services';
// State
import { AppState } from '../../../core/reducers';
// Selectors
// Actions
import {
  AllAgreementTemplatesLoaded,
  AllAgreementTemplatesRequested,
  AgreementTemplateActionTypes,
  AgreementTemplatesPageRequested,
  AgreementTemplatesPageLoaded,
  AgreementTemplatesPageToggleLoading,
  AgreementTemplateDeleted,
  AgreementTemplateActivated,
  AgreementTemplateOnServerCreated,
  AgreementTemplateCreated,
  AgreementTemplatesActionToggleLoading,
  AgreementTemplatesPageCancelled,
  ThrowAgreementTemplateError,
  FireAgreementTemplateSuccess,
  AgreementTemplateOnServerDeleted,
  AgreementTemplateOnServerActivated,
} from '../_actions/agreement-template.actions';
import { currentUser } from '../../auth/_selectors/auth.selectors';

@Injectable()
export class AgreementTemplateEffects {
  showPageLoadingDistpatcher = new AgreementTemplatesPageToggleLoading({ isLoading: true });
  hidePageLoadingDistpatcher = new AgreementTemplatesPageToggleLoading({ isLoading: false });

  showActionLoadingDistpatcher = new AgreementTemplatesActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new AgreementTemplatesActionToggleLoading({ isLoading: false });

  @Effect()
  loadAllAgreementTemplates$ = this.actions$.pipe(
    ofType<AllAgreementTemplatesRequested>(
      AgreementTemplateActionTypes.AllAgreementTemplatesRequested,
    ),
    withLatestFrom(this.store.pipe(select(currentUser))), // Get current user to check for companyId
    filter(([, user]) => !!user?.companyID), // Only proceed if companyID is available
    mergeMap(([, user]) => {
      // Proceed to make the request if companyID is available
      return this.agreementService.getAllAgreementTemplates(user.companyID).pipe(
        map((agreementTemplates) => {
          // Store the templates in localStorage
          localStorage.setItem('allAgreementTemplates', JSON.stringify(agreementTemplates));
          // Dispatch the AllAgreementTemplatesLoaded action with the fetched templates
          return new AllAgreementTemplatesLoaded({ agreementTemplates });
        }),
      );
    }),
  );

  @Effect()
  loadAgreementTemplatesPage$ = this.actions$.pipe(
    ofType<AgreementTemplatesPageRequested>(
      AgreementTemplateActionTypes.AgreementTemplatesPageRequested,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.agreementService.findAgreementTemplates(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin(requestToServer, lastQuery).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res.length === 2) {
            const result = res[0];
            return new AgreementTemplatesPageLoaded({
              agreementTemplates: result.data,
              totalCount: result.count,
            });
          } else {
            this.store.dispatch(new ThrowAgreementTemplateError({ error: res }));
            return new AgreementTemplatesPageCancelled();
          }
        }),
      );
    }),
  );

  @Effect()
  deleteAgreementTemplate$ = this.actions$.pipe(
    ofType<AgreementTemplateOnServerDeleted>(
      AgreementTemplateActionTypes.AgreementTemplateOnServerDeleted,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.agreementService.deleteAgreementTemplate(payload.id, payload.office_id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowAgreementTemplateError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireAgreementTemplateSuccess({
                action_type: AgreementTemplateActionTypes.AgreementTemplateDeleted,
              }),
            );
            return new AgreementTemplateDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  activateAgreementTemplate$ = this.actions$.pipe(
    ofType<AgreementTemplateOnServerActivated>(
      AgreementTemplateActionTypes.AgreementTemplateOnServerActivated,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.agreementService.activateAgreementTemplate(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowAgreementTemplateError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireAgreementTemplateSuccess({
                action_type: AgreementTemplateActionTypes.AgreementTemplateActivated,
              }),
            );
            return new AgreementTemplateActivated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  createAgreementTemplate$ = this.actions$.pipe(
    ofType<AgreementTemplateOnServerCreated>(
      AgreementTemplateActionTypes.AgreementTemplateOnServerCreated,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.agreementService.createTemplate(payload.agreementTemplate, payload.officeId).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (!res.id) {
            return new ThrowAgreementTemplateError({ error: res });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireAgreementTemplateSuccess({
                action_type: AgreementTemplateActionTypes.AgreementTemplateCreated,
              }),
            );
            return new AgreementTemplateCreated({ agreementTemplate: res });
          }
        }),
      );
    }),
  );

  @Effect()
  init$ = this.actions$.pipe(
    ofType(ROOT_EFFECTS_INIT),
    map(() => new AllAgreementTemplatesRequested()),
  );

  constructor(
    private actions$: Actions,
    private agreementService: AgreementService,
    private store: Store<AppState>,
  ) {}
}
